import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, toast } from '../App';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Upload, Trash2, FileText, Globe, Eye, EyeOff, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// Extracted sub-components
import {
  UserInfoCard,
  DisplayPreferencesCard,
  StyleDocumentsCard,
  AuthorSignatureCard,
  AdminCredentialsCard,
  EmailNotificationSettingsCard,
  ConfigExportImportCard,
  AdminToolsCard,
  HardResetCard,
  InterviewTrackingCard,
  LLMArtifactsCard,
  PromptsManagementCard,
  iOSShortcutCard as IOSShortcutCard,
  BulkImportCard,
  ApiKeysCard,
  MCPImportsCard,
} from '../components/Profile';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [bookTitle, setBookTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [authorBio, setAuthorBio] = useState('');
  
  // WordPress credentials
  const [sites, setSites] = useState([]);
  const [wpCredentials, setWpCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [savingCreds, setSavingCreds] = useState({});
  
  // Admin WordPress Credentials (Amiraux only)
  const [adminCredentials, setAdminCredentials] = useState({});
  const [showAdminPasswords, setShowAdminPasswords] = useState({});
  const [savingAdminCreds, setSavingAdminCreds] = useState({});
  const [showAdminCredentials, setShowAdminCredentials] = useState(false);
  
  // Collapsible sections
  const [showCredentials, setShowCredentials] = useState(false);
  const [authorMappings, setAuthorMappings] = useState({}); // {site_id: {wp_author_id, wp_author_name}}
  const [authorEdits, setAuthorEdits] = useState({}); // {site_id: {id:'', name:''}}
  
  // Config export/import
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [hardResetConfirm, setHardResetConfirm] = useState(false);

  // Email notification settings (Amiraux only)
  const [showEmailNotifSettings, setShowEmailNotifSettings] = useState(false);
  const [emailNotifSettings, setEmailNotifSettings] = useState(null);
  const [editingNotifEmails, setEditingNotifEmails] = useState({});
  const [newNotifEmail, setNewNotifEmail] = useState({});
  const [savingNotifSettings, setSavingNotifSettings] = useState({});

  // Load admin credentials for WordPress sites (Amiraux only)
  const loadAdminCredentials = useCallback(async () => {
    if (user?.grade !== 'amiral') return;
    try {
      const { data } = await axios.get(`${API}/admin/wordpress-admin-credentials`);
      const credsMap = {};
      (data || []).forEach(cred => {
        credsMap[cred.site_id] = {
          username: cred.username,
          password: cred.password
        };
      });
      setAdminCredentials(credsMap);
    } catch (error) {
      console.error('Error loading admin credentials:', error);
    }
  }, [user?.grade]);

  // Load email notification settings (Amiraux only)
  const loadEmailNotifSettings = useCallback(async () => {
    if (user?.grade !== 'amiral') return;
    try {
      const { data } = await axios.get(`${API}/admin/email-notification-settings`);
      setEmailNotifSettings(data);
      // Initialiser les états d'édition
      setEditingNotifEmails({
        interview_notification: [...(data.interview_settings?.notification_emails || [])],
        partenaire_mandatory_cc: [...(data.partenaire_settings?.mandatory_cc || [])],
        partenaire_super_users: [...(data.partenaire_settings?.super_users || [])],
      });
    } catch (error) {
      console.error('Error loading email notification settings:', error);
    }
  }, [user?.grade]);

  // Fonctions de chargement converties en useCallback pour stabilité
  const loadBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  }, []);

  const loadWpCredentials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/wordpress-credentials`);
      const credsMap = {};
      response.data.forEach(cred => {
        credsMap[cred.site_id] = {
          username: cred.wp_username,
          password: cred.wp_password
        };
      });
      setWpCredentials(credsMap);
    } catch (error) {
      console.error('Error loading WP credentials:', error);
    }
  }, []);

  const loadAuthorMappings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/user-author-mappings`);
      const map = {};
      const edits = {};
      (data || []).forEach(m => {
        map[m.site_id] = { wp_author_id: m.wp_author_id, wp_author_name: m.wp_author_name, wp_author_slug: m.wp_author_slug };
        edits[m.site_id] = { query: m.wp_author_name || '', candidates: null, error: null };
      });
      setAuthorMappings(map);
      setAuthorEdits(edits);
    } catch (e) {
      console.error('Error loading author mappings:', e);
    }
  }, []);

  useEffect(() => {
    loadBooks();
    loadSites();
    loadWpCredentials();
    loadAuthorMappings();
    loadAdminCredentials();
    loadEmailNotifSettings();
  }, [loadBooks, loadSites, loadWpCredentials, loadAuthorMappings, loadAdminCredentials, loadEmailNotifSettings]);

  const addNotifEmail = (listKey) => {
    const email = (newNotifEmail[listKey] || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Adresse email invalide');
      return;
    }
    const currentList = editingNotifEmails[listKey] || [];
    if (currentList.includes(email)) {
      toast.error('Cette adresse est déjà dans la liste');
      return;
    }
    setEditingNotifEmails(prev => ({
      ...prev,
      [listKey]: [...currentList, email]
    }));
    setNewNotifEmail(prev => ({ ...prev, [listKey]: '' }));
  };

  const removeNotifEmail = (listKey, email) => {
    setEditingNotifEmails(prev => ({
      ...prev,
      [listKey]: (prev[listKey] || []).filter(e => e !== email)
    }));
  };

  const saveNotifEmails = async (listKey) => {
    const emails = editingNotifEmails[listKey] || [];
    let settingsType, field;
    
    if (listKey === 'interview_notification') {
      settingsType = 'interview';
      field = 'notification_emails';
    } else if (listKey === 'partenaire_mandatory_cc') {
      settingsType = 'partenaire';
      field = 'mandatory_cc';
    } else if (listKey === 'partenaire_super_users') {
      settingsType = 'partenaire';
      field = 'super_users';
    } else {
      return;
    }
    
    setSavingNotifSettings(prev => ({ ...prev, [listKey]: true }));
    try {
      await axios.post(`${API}/admin/email-notification-settings`, null, {
        params: { settings_type: settingsType, field: field },
        data: { value: emails }
      });
      toast.success('Destinataires mis à jour');
      loadEmailNotifSettings(); // Recharger
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSavingNotifSettings(prev => ({ ...prev, [listKey]: false }));
  };

  const updateAdminCredential = (siteId, field, value) => {
    setAdminCredentials(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] || {}), [field]: value }
    }));
  };

  const saveAdminCredentials = async (siteId) => {
    const cred = adminCredentials[siteId] || {};
    if (!cred.username?.trim() || !cred.password?.trim()) {
      toast.error('Renseignez le login et le mot de passe admin');
      return;
    }
    setSavingAdminCreds(prev => ({ ...prev, [siteId]: true }));
    try {
      await axios.post(`${API}/admin/wordpress-admin-credentials`, {
        site_id: siteId,
        username: cred.username,
        password: cred.password
      });
      toast.success('Identifiants admin enregistrés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSavingAdminCreds(prev => ({ ...prev, [siteId]: false }));
  };

  const updateAuthorEdit = (siteId, field, value) => {
    setAuthorEdits(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] || { query: '' }), [field]: value, candidates: null, error: null }
    }));
  };

  const pickAuthorCandidate = (siteId, candidate) => {
    setAuthorEdits(prev => ({
      ...prev,
      [siteId]: { query: candidate.slug || candidate.name, candidates: null, error: null }
    }));
    toast.info(`Sélectionné : ${candidate.name} (slug: ${candidate.slug}). Cliquez "Enregistrer" pour valider.`);
  };

  const saveSiteCredentialsAndAuthor = async (siteId) => {
    const cred = wpCredentials[siteId] || {};
    const author = authorEdits[siteId] || {};
    const hasCreds = (cred.username || '').trim() || (cred.password || '').trim();
    const credValid = !hasCreds || ((cred.username || '').trim() && (cred.password || '').trim());
    if (!credValid) {
      toast.error('Login et mot de passe doivent être remplis ensemble (ou laissés vides tous les deux)');
      return;
    }
    const authorQuery = (author.query || '').trim();
    if (!hasCreds && !authorQuery) {
      toast.error('Renseignez au moins le nom d\'auteur (ou les identifiants complets)');
      return;
    }

    setSavingCreds(prev => ({ ...prev, [siteId]: true }));
    try {
      // 1. Identifiants (optionnels)
      const u = encodeURIComponent((cred.username || '').trim());
      const p = encodeURIComponent((cred.password || '').trim());
      await axios.post(`${API}/user/wordpress-credentials?site_id=${siteId}&wp_username=${u}&wp_password=${p}`);

      // 2. Résolution + Mapping auteur (si une query est saisie)
      if (authorQuery) {
        let resolved;
        try {
          const { data } = await axios.post(`${API}/wordpress-sites/${siteId}/resolve-author`, { query: authorQuery });
          resolved = data;
        } catch (resolveErr) {
          if (resolveErr.response?.status === 409 && resolveErr.response.data?.candidates) {
            // Plusieurs candidats : afficher pour aider l'utilisateur
            setAuthorEdits(prev => ({
              ...prev,
              [siteId]: { ...(prev[siteId] || {}), candidates: resolveErr.response.data.candidates, error: resolveErr.response.data.detail }
            }));
            toast.error(resolveErr.response.data.detail || 'Plusieurs auteurs correspondent — précisez le slug ou l\'ID');
            setSavingCreds(prev => ({ ...prev, [siteId]: false }));
            return;
          }
          toast.error(resolveErr.response?.data?.detail || 'Auteur introuvable sur WordPress');
          setSavingCreds(prev => ({ ...prev, [siteId]: false }));
          return;
        }

        await axios.post(`${API}/user-author-mappings`, {
          site_id: siteId,
          wp_author_id: resolved.id,
          wp_author_name: resolved.name,
          wp_author_slug: resolved.slug || ''
        });
        setAuthorMappings(prev => ({
          ...prev,
          [siteId]: { wp_author_id: resolved.id, wp_author_name: resolved.name, wp_author_slug: resolved.slug }
        }));
        setAuthorEdits(prev => ({
          ...prev,
          [siteId]: { query: resolved.name, candidates: null, error: null, lastResolved: resolved }
        }));
        toast.success(`Auteur résolu : ${resolved.name} (ID #${resolved.id})`);
      } else if (hasCreds) {
        toast.success('Identifiants WordPress enregistrés');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingCreds(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const updateCredential = (siteId, field, value) => {
    setWpCredentials(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [field]: value
      }
    }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false
  });

  const handleUpload = async () => {
    if (!bookTitle || !selectedFile) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', bookTitle);
      formData.append('file', selectedFile);

      await axios.post(`${API}/books`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document ajouté');
      setShowUpload(false);
      setBookTitle('');
      setSelectedFile(null);
      loadBooks();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Supprimer ce document ?')) return;

    try {
      await axios.delete(`${API}/books/${bookId}`);
      toast.success('Document supprimé');
      loadBooks();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/config/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auto-edito-config-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Configuration exportee');
    } catch (e) { toast.error('Erreur export'); }
    finally { setExporting(false); }
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        setImportData(parsed);
        setShowImportDialog(true);
      } catch { toast.error('Fichier JSON invalide'); setImportFile(null); }
    };
    reader.readAsText(file);
  };

  const handleImport = async (mode) => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/config/import`, { mode, data: importData });
      const s = res.data.imported;
      const total = Object.values(s).reduce((a, b) => a + b, 0);
      toast.success(`Import OK (${mode === 'replace' ? 'remplacement' : 'fusion'}) : ${total} éléments traités`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportData(null);
      loadSites();
    } catch (e) { toast.error('Erreur import'); }
    finally { setImporting(false); }
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-orbitron tracking-widest uppercase text-sky-400 text-glow mb-2">
            Mon Profil
          </h1>
          <p className="text-base md:text-lg text-slate-400 font-rajdhani tracking-wide">
            Paramètres et style d&apos;écriture
          </p>
        </div>

        <div className="space-y-6">
          {/* User Info Card */}
          <UserInfoCard user={user} onLogout={handleLogout} />

          {/* Display Preferences Card */}
          <DisplayPreferencesCard isDark={isDark} toggleTheme={toggleTheme} />

          {/* iOS Shortcut Card */}
          <IOSShortcutCard />

          {/* Style Documents Card */}
          <StyleDocumentsCard
            books={books}
            loading={loading}
            showUpload={showUpload}
            setShowUpload={setShowUpload}
            bookTitle={bookTitle}
            setBookTitle={setBookTitle}
            selectedFile={selectedFile}
            uploading={uploading}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            onUpload={handleUpload}
            onDelete={handleDelete}
          />

          {/* Ma signature WordPress - Pour TOUS les utilisateurs */}
          <AuthorSignatureCard
            sites={sites}
            showCredentials={showCredentials}
            setShowCredentials={setShowCredentials}
            authorEdits={authorEdits}
            authorMappings={authorMappings}
            savingCreds={savingCreds}
            onUpdateAuthorEdit={updateAuthorEdit}
            onPickAuthorCandidate={pickAuthorCandidate}
            onSaveCredentials={saveSiteCredentialsAndAuthor}
          />

          {/* Admin WordPress Credentials - AMIRAUX UNIQUEMENT */}
          {user?.grade === 'amiral' && (
            <AdminCredentialsCard
              sites={sites}
              showAdminCredentials={showAdminCredentials}
              setShowAdminCredentials={setShowAdminCredentials}
              adminCredentials={adminCredentials}
              showAdminPasswords={showAdminPasswords}
              setShowAdminPasswords={setShowAdminPasswords}
              savingAdminCreds={savingAdminCreds}
              onUpdateAdminCredential={updateAdminCredential}
              onSaveAdminCredentials={saveAdminCredentials}
            />
          )}

          {/* Destinataires de notification - AMIRAUX UNIQUEMENT */}
          {user?.grade === 'amiral' && (
            <EmailNotificationSettingsCard
              showEmailNotifSettings={showEmailNotifSettings}
              setShowEmailNotifSettings={setShowEmailNotifSettings}
              emailNotifSettings={emailNotifSettings}
              editingNotifEmails={editingNotifEmails}
              newNotifEmail={newNotifEmail}
              setNewNotifEmail={setNewNotifEmail}
              savingNotifSettings={savingNotifSettings}
              onAddNotifEmail={addNotifEmail}
              onRemoveNotifEmail={removeNotifEmail}
              onSaveNotifEmails={saveNotifEmails}
            />
          )}

          {/* Config Export/Import (Amiral only) */}
          {user?.grade === 'amiral' && (
            <ConfigExportImportCard
              exporting={exporting}
              importing={importing}
              importFile={importFile}
              importData={importData}
              showImportDialog={showImportDialog}
              setShowImportDialog={setShowImportDialog}
              onExport={handleExport}
              onImportFileSelect={handleImportFileSelect}
              onImport={handleImport}
              onCloseDialog={() => { setShowImportDialog(false); setImportFile(null); setImportData(null); }}
            />
          )}

          {/* Tracking Auto-Interviews — Amiraux uniquement */}
          {user?.grade === 'amiral' && <InterviewTrackingCard />}

          {/* Artefacts LLM — Amiraux uniquement */}
          {user?.grade === 'amiral' && <LLMArtifactsCard />}

          {/* Import en masse — Amiraux uniquement */}
          {user?.grade === 'amiral' && <BulkImportCard user={user} />}

          {/* Clés API — Amiraux uniquement */}
          {user?.grade === 'amiral' && <ApiKeysCard user={user} />}

          {/* Dashboard MCP — Amiraux uniquement */}
          {user?.grade === 'amiral' && <MCPImportsCard user={user} />}

          {/* Outils d'administration — Amiraux uniquement */}
          {user?.grade === 'amiral' && <AdminToolsCard navigate={navigate} />}

          {/* Gestion des Prompts — Amiraux uniquement */}
          {user?.grade === 'amiral' && <PromptsManagementCard navigate={navigate} />}

          {/* Hard Reset — Amiraux uniquement */}
          {user?.grade === 'amiral' && (
            <HardResetCard
              hardResetConfirm={hardResetConfirm}
              setHardResetConfirm={setHardResetConfirm}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
